import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * ThemeContext — admin/broker-only dark-mode toggle.
 *
 * Behaviour:
 * - Only "admin" and "broker" roles can toggle the theme. Clients
 *   (role: "user") and introducers always see the light brand palette
 *   so customer-facing surfaces stay on-brand.
 * - The choice is persisted per user in localStorage under
 *   `solara-theme-${userId}`, so flipping the toggle on one device
 *   carries over the next time that user signs in on that device.
 * - We apply `data-theme="dark"` to <html> when the active user is
 *   staff AND has chosen dark. Anything else (logged out, client,
 *   introducer, staff who picked light) clears the attribute. The
 *   matching CSS lives in index.css under [data-theme="dark"].
 *
 * Why <html> and not a wrapper div: shadcn portals (dialogs, dropdowns,
 * toasts) render at the body root, outside the React tree. Putting the
 * attribute on documentElement guarantees those portals inherit the
 * dark palette too.
 */

const ThemeContext = createContext(null);

const STORAGE_PREFIX = "solara-theme-";
const STAFF_ROLES = new Set(["admin", "broker"]);

const readStoredTheme = (userId) => {
  if (!userId || typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    return v === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const writeStoredTheme = (userId, theme) => {
  if (!userId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, theme);
  } catch {
    /* localStorage unavailable (private mode etc) — silently degrade. */
  }
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const isStaff = !!user && STAFF_ROLES.has(user.role);

  // Hydrate from localStorage the moment a staff user is known.
  // Non-staff sessions are always "light" — we never read their storage.
  const [theme, setThemeState] = useState(() =>
    isStaff ? readStoredTheme(user.id) : "light"
  );

  // Re-hydrate if the active user changes (login/logout/account switch).
  useEffect(() => {
    if (isStaff) {
      setThemeState(readStoredTheme(user.id));
    } else {
      setThemeState("light");
    }
  }, [user?.id, isStaff]);

  // Reflect onto <html data-theme="...">, but only when the active user
  // is staff. Anything else clears the attribute so client-facing pages
  // are never dark.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (isStaff && theme === "dark") {
      root.setAttribute("data-theme", "dark");
    } else {
      root.removeAttribute("data-theme");
    }
    // Cleanup on unmount (e.g. SSR/hot reload edge cases).
    return () => {
      if (root.getAttribute("data-theme") === "dark") {
        root.removeAttribute("data-theme");
      }
    };
  }, [theme, isStaff]);

  const setTheme = useCallback(
    (next) => {
      if (!isStaff) return;
      const v = next === "dark" ? "dark" : "light";
      setThemeState(v);
      writeStoredTheme(user.id, v);
    },
    [isStaff, user?.id]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = {
    theme,
    setTheme,
    toggleTheme,
    // Components should check `canToggle` before rendering the switch.
    canToggle: isStaff,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
};
